import {
  ITaskDefinitionGrid,
  TaskGridCellType,
} from "@evennt-tech/api-v2-core";
import {
  ICreateTaskGridCellInput,
  ICreateTaskGridInput,
  IDrawerTaskInstanceGridPayload,
  ISubmitTaskInstanceGridInput,
} from "@evennt-tech/api-v2-external";
import uniqid from "uniqid";
import { IGridSchema } from "ts/api/grid-schema";
import { GridForm } from "ts/api/task";

/**
 * Maps between the normalized grid wire contract and the FE editor / response
 * form drafts. The wire side is fully typed by core/external; the only FE
 * concession is the editor's Record-keyed `cells` container (see
 * `ts/api/grid-schema.ts`).
 */

/** GET (`ITaskDefinitionGrid`, flat columns/rows/cells with ids) → editor draft. */
export const definitionGridToSchema = (
  grid?: ITaskDefinitionGrid | null
): IGridSchema | null => {
  if (!grid) {
    return null;
  }
  const columns = [...(grid.columns ?? [])].sort(
    (a, b) => a.position - b.position
  );
  const rows = [...(grid.rows ?? [])].sort((a, b) => a.position - b.position);
  const colIndexById = new Map(columns.map((c, i) => [c.id, i]));
  const rowIndexById = new Map(rows.map((r, i) => [r.id, i]));

  const cells: Record<string, ICreateTaskGridCellInput[]> = {};
  const cellIds: string[][] = [];
  rows.forEach((_, rIdx) => {
    cells[String(rIdx)] = [];
    cellIds[rIdx] = [];
  });

  for (const cell of grid.cells ?? []) {
    const rIdx = rowIndexById.get(cell.rowId);
    const cIdx = colIndexById.get(cell.columnId);
    if (rIdx == null || cIdx == null) {
      continue;
    }
    cells[String(rIdx)][cIdx] = {
      type: cell.type,
      required: cell.required,
      options: cell.options,
      optionsTranslationIds: cell.optionsTranslationIds,
      fileTypes: cell.fileTypes,
      fileSizeLimitMb: cell.fileSizeLimitMb,
      maxNumberOfFiles: cell.maxNumberOfFiles,
      defaultValue: cell.defaultValue,
    };
    cellIds[rIdx][cIdx] = cell.id;
  }

  return {
    name: grid.name,
    includeRowTitles: grid.includeRowTitles,
    allowToAddRows: grid.allowToAddRows,
    columns: columns.map((c) => ({
      name: c.name,
      axisKey: c.axisKey ?? uniqid(),
    })),
    rows: rows.map((r) => ({ name: r.name, axisKey: r.axisKey ?? uniqid() })),
    cells,
    wasSaved: true,
    columnIds: columns.map((c) => c.id),
    rowIds: rows.map((r) => r.id),
    cellIds,
  };
};

/** Editor draft → create/update payload (Record cells → row-major matrix). */
export const schemaToCreateInput = (
  schema: IGridSchema
): ICreateTaskGridInput => {
  const rowCount = schema.rows.length;
  const cells: ICreateTaskGridCellInput[][] = [];
  for (let r = 0; r < rowCount; r += 1) {
    cells.push(schema.cells[String(r)] ?? []);
  }
  return {
    name: schema.name,
    includeRowTitles: schema.includeRowTitles,
    allowToAddRows: schema.allowToAddRows,
    columns: schema.columns.map((column) => ({
      name: column.name,
      axisKey: column.axisKey ?? uniqid(),
    })),
    rows: schema.rows.map((row) => ({
      name: row.name,
      axisKey: row.axisKey ?? uniqid(),
    })),
    cells,
  };
};

/**
 * Drawer read (`IDrawerTaskInstanceGridPayload`, flat cells keyed by
 * `gridCellId` / `responseRowId` + `columnId`) → positional FE answer grid.
 * Needs the schema (with `columnIds` / `cellIds` threaded) to place each cell
 * back into its `[rowIdx][colIdx]` slot. Exhibitor-added rows append after the
 * schema rows in `position` order; their labels surface as `additionalRowNames`.
 */
export const drawerGridToFormAnswer = (
  schema: IGridSchema | null,
  payload?: IDrawerTaskInstanceGridPayload | null
): {
  gridSchema: IGridSchema | null;
  grid: GridForm["grid"];
  additionalRowNames: string[];
} => {
  if (!schema) {
    return { gridSchema: null, grid: [], additionalRowNames: [] };
  }
  const colCount = schema.columns.length;
  const schemaRowCount = schema.rows.length;

  const colIndexById = new Map(
    (schema.columnIds ?? []).map((id, i) => [id, i])
  );
  const cellPos = new Map<string, [number, number]>();
  (schema.cellIds ?? []).forEach((row, rIdx) =>
    row?.forEach((id, cIdx) => {
      if (id) {
        cellPos.set(id, [rIdx, cIdx]);
      }
    })
  );

  const addedRows = [...(payload?.rows ?? [])].sort(
    (a, b) => a.position - b.position
  );
  const addedRowIndexById = new Map(
    addedRows.map((r, i) => [r.id, schemaRowCount + i])
  );
  const totalRows = schemaRowCount + addedRows.length;

  // Seed every slot from the schema cell type so blank cells still render in
  // the right input mode (text vs attachments).
  const grid: GridForm["grid"] = [];
  for (let r = 0; r < totalRows; r += 1) {
    const templateRow =
      schema.cells[String(Math.min(r, schemaRowCount - 1))] ?? [];
    grid.push({
      cells: Array.from({ length: colCount }, (_, c) =>
        templateRow[c]?.type === TaskGridCellType.FILES
          ? { type: "attachments", attachments: [] }
          : { type: "text", text: "" }
      ),
    });
  }

  for (const cell of payload?.cells ?? []) {
    let pos: [number, number] | undefined;
    if (cell.gridCellId && cellPos.has(cell.gridCellId)) {
      pos = cellPos.get(cell.gridCellId);
    } else if (cell.responseRowId != null) {
      const rIdx = addedRowIndexById.get(cell.responseRowId);
      const cIdx =
        cell.columnId != null ? colIndexById.get(cell.columnId) : undefined;
      if (rIdx != null && cIdx != null) {
        pos = [rIdx, cIdx];
      }
    }
    // Fallback to the immutable snapshot position (captured at submit time).
    // Reliable when the gridCellId link can't resolve — e.g. after a definition
    // edit (SET NULL) or an id mismatch — so the previous version's values
    // still land in the right cells when re-opened in the editor.
    if (!pos) {
      const rIdx = cell.rowPosition;
      const cIdx = cell.columnPosition;
      if (
        Number.isInteger(rIdx) &&
        Number.isInteger(cIdx) &&
        grid[rIdx]?.cells[cIdx]
      ) {
        pos = [rIdx, cIdx];
      }
    }
    if (!pos) {
      continue;
    }
    const [rIdx, cIdx] = pos;
    if (!grid[rIdx]?.cells[cIdx]) {
      continue;
    }
    grid[rIdx].cells[cIdx] =
      cell.type === TaskGridCellType.FILES
        ? {
            type: "attachments",
            attachments: (cell.attachments ?? []).map((a) => ({
              id: a.id,
              title: a.name ?? "",
              url: a.url ?? "",
              provider: a.provider,
              file: { name: a.name ?? "", size: a.size ?? 0 },
            })),
          }
        : { type: "text", text: cell.text ?? "" };
  }

  // Expand the schema's cell matrix for exhibitor-added rows so the renderer
  // (which derives row count from `gridSchema.cells`) emits inputs for them.
  let gridSchema = schema;
  if (addedRows.length) {
    const template =
      schema.cells[String(schemaRowCount - 1)] ?? schema.cells["0"] ?? [];
    const expandedCells: Record<string, typeof template> = { ...schema.cells };
    for (let i = 0; i < addedRows.length; i += 1) {
      expandedCells[String(schemaRowCount + i)] = template.map((c) => ({
        ...c,
      }));
    }
    gridSchema = { ...schema, cells: expandedCells };
  }

  return { gridSchema, grid, additionalRowNames: addedRows.map((r) => r.name) };
};

/**
 * Build the grid structure + answer ENTIRELY from the per-cell immutable
 * snapshot (column/row label + order captured at submit time). Used for
 * read-only DISPLAY (current answer + history) so a later definition edit
 * never changes how a past version renders. Returns `gridSchema: null` when
 * there's no answer yet (caller falls back to the live definition).
 */
export const snapshotGridToForm = (
  payload?: IDrawerTaskInstanceGridPayload | null
): { gridSchema: IGridSchema | null; grid: GridForm["grid"] } => {
  const cells = payload?.cells ?? [];
  if (!cells.length) {
    return { gridSchema: null, grid: [] };
  }

  const colNameByPos = new Map<number, string>();
  const rowNameByPos = new Map<number, string>();
  for (const c of cells) {
    colNameByPos.set(c.columnPosition, c.columnName);
    rowNameByPos.set(c.rowPosition, c.rowName);
  }
  const colPositions = Array.from(colNameByPos.keys()).sort((a, b) => a - b);
  const rowPositions = Array.from(rowNameByPos.keys()).sort((a, b) => a - b);
  const colIndexByPos = new Map(colPositions.map((p, i) => [p, i]));
  const rowIndexByPos = new Map(rowPositions.map((p, i) => [p, i]));

  const cellByPos = new Map<
    string,
    IDrawerTaskInstanceGridPayload["cells"][number]
  >();
  for (const c of cells) {
    cellByPos.set(`${c.rowPosition}:${c.columnPosition}`, c);
  }

  const toFormCell = (
    c?: IDrawerTaskInstanceGridPayload["cells"][number]
  ): GridForm["grid"][number]["cells"][number] =>
    c?.type === TaskGridCellType.FILES
      ? {
          type: "attachments",
          attachments: (c.attachments ?? []).map((a) => ({
            id: a.id,
            title: a.name ?? "",
            url: a.url ?? "",
            provider: a.provider,
            file: { name: a.name ?? "", size: a.size ?? 0 },
          })),
        }
      : { type: "text", text: c?.text ?? "" };

  const grid: GridForm["grid"] = rowPositions.map((rp) => ({
    cells: colPositions.map((cp) => toFormCell(cellByPos.get(`${rp}:${cp}`))),
  }));

  const schemaCells: Record<string, ICreateTaskGridCellInput[]> = {};
  rowPositions.forEach((rp, ri) => {
    schemaCells[String(ri)] = colPositions.map((cp) => {
      const c = cellByPos.get(`${rp}:${cp}`);
      const type = c?.type ?? TaskGridCellType.TEXT;
      // OPTIONS cells render a (read-only) MUI Select — it only shows the
      // selected value when that value exists as an option. We don't snapshot
      // the full option list, so seed it with the picked value so history
      // displays what was chosen.
      if (type === TaskGridCellType.OPTIONS && c?.text) {
        return { type, options: [c.text] };
      }
      return { type };
    });
  });

  const rows = rowPositions.map((p) => ({ name: rowNameByPos.get(p) ?? "" }));

  const gridSchema: IGridSchema = {
    name: "",
    columns: colPositions.map((p) => ({ name: colNameByPos.get(p) ?? "" })),
    rows,
    cells: schemaCells,
    // Derived, NOT hardcoded `true`: the answer snapshot has no
    // `includeRowTitles` of its own, but it does snapshot each row's label —
    // and only a "ROWS & COLUMNS" table has labelled rows ("ONLY COLUMNS"
    // leaves them empty). Hardcoding `true` resurrected the row-title column
    // for every only-columns table read through a snapshot (history modal,
    // preview) and, worse, leaked into the form value that `Columns` fell back
    // to. Callers with the live definition still graft its real flag on top
    // (see `GridPreview`).
    includeRowTitles: rows.some((row) => !!row.name.trim()),
    allowToAddRows: false,
  };

  return { gridSchema, grid };
};

/**
 * `required` is a DEFINITION property — the immutable answer snapshot doesn't
 * carry it, so a snapshot-reconstructed schema (`snapshotGridToForm`) loses
 * the mandatory-cell marker and every cell renders as optional. Overlay it
 * from the live definition by row/column position so read-only grids (drawer
 * ResponseBlock, standalone GridPreview page) still distinguish required from
 * optional cells. Best-effort: if the definition changed shape since the
 * answer, cells that no longer line up just show no marker (never a wrong
 * one). Exhibitor-added rows (beyond the definition's rows) inherit the LAST
 * definition row's config — the same rule the fill popup applies via
 * `drawerGridToFormAnswer`.
 */
export const overlayRequiredFromDefinition = (
  snapshotSchema: IGridSchema | null | undefined,
  definitionSchema: IGridSchema | null | undefined
): void => {
  if (!snapshotSchema?.cells || !definitionSchema?.cells) {
    return;
  }
  const schemaRowCount =
    definitionSchema.rows?.length ?? Object.keys(definitionSchema.cells).length;
  const templateRow =
    definitionSchema.cells[String(schemaRowCount - 1)] ??
    definitionSchema.cells["0"];
  for (const [rowKey, snapRow] of Object.entries(snapshotSchema.cells)) {
    const defRow = definitionSchema.cells[rowKey] ?? templateRow;
    if (!Array.isArray(snapRow) || !Array.isArray(defRow)) {
      continue;
    }
    snapRow.forEach((snapCell, cIdx) => {
      if (snapCell && defRow[cIdx]) {
        snapCell.required = defRow[cIdx].required;
      }
    });
  }
};

/**
 * Positional FE answer grid → submit payload. Threads each cell's `columnId`
 * (from `schema.columnIds`) and `gridCellId` (from `schema.cellIds`, omitted
 * for exhibitor-added rows). FILES cells carry pre-uploaded storage save-tokens
 * stashed on the form cell by `processGrid`.
 */
export const formAnswerToSubmitInput = (
  schema: IGridSchema | null | undefined,
  grid: GridForm["grid"],
  additionalRowNames: string[] = []
): ISubmitTaskInstanceGridInput => {
  const schemaRowCount = schema?.rows?.length ?? 0;
  const columnIds = schema?.columnIds ?? [];
  const cellIds = schema?.cellIds ?? [];

  const rows = (grid ?? []).map((row, rIdx) => {
    const isAddedRow = rIdx >= schemaRowCount;
    const cells = (row?.cells ?? []).map((cell, cIdx) => {
      const columnId = columnIds[cIdx] ?? "";
      const gridCellId = isAddedRow ? null : cellIds[rIdx]?.[cIdx] ?? null;
      const schemaType =
        schema?.cells?.[String(Math.min(rIdx, schemaRowCount - 1))]?.[cIdx]
          ?.type;

      // `cell` may be undefined: a draft is partial and react-hook-form leaves
      // holes in the array for cell inputs that never mounted. Treat the SCHEMA
      // cell type as authoritative (fall back to the form cell) so an untouched
      // cell still submits with the correct type instead of crashing on
      // `cell.type`.
      const isFilesCell =
        schemaType === TaskGridCellType.FILES || cell?.type === "attachments";

      if (isFilesCell) {
        // Narrow to the attachments variant to read files/tokens; the cell may
        // be a text placeholder or undefined for an untouched FILES cell.
        const filesCell = cell?.type === "attachments" ? cell : undefined;
        const freshTokens = filesCell?.tokens ?? [];
        // Ids of previously-uploaded files still present in the cell — these
        // must survive an edit that only ADDS a new file (existing files carry
        // a `url`; freshly-picked blobs don't, they go up as `freshTokens`).
        const keepFileIds = (filesCell?.attachments ?? [])
          .filter((a) => !!a.url)
          .map((a) => a.id);

        // Cell never mounted in a partial draft → leave both undefined so the
        // BE copies ALL prior files (it never trusts a client file id and must
        // not clear an untouched cell). Otherwise send the explicit desired
        // set: keep `keepFileIds` + add `freshTokens` (both `[]` clears it).
        if (!cell) {
          return { columnId, gridCellId, type: TaskGridCellType.FILES };
        }
        return {
          columnId,
          gridCellId,
          type: TaskGridCellType.FILES,
          keepFileIds,
          fileTokens: freshTokens,
        };
      }
      return {
        columnId,
        gridCellId,
        type: schemaType ?? TaskGridCellType.TEXT,
        text: cell?.text ?? null,
      };
    });

    return isAddedRow
      ? { name: additionalRowNames[rIdx - schemaRowCount] ?? "", cells }
      : { cells };
  });

  return { rows };
};
